import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { semanticColors, colorTokens } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { billingService } from '@/services/billingService';
import { toastUtils } from '@/common/utils/toastUtils';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

type CallbackType = 'success' | 'fail';

interface BillingCallbackPageProps {
  type: CallbackType;
}

const BillingCallbackPage: React.FC<BillingCallbackPageProps> = ({ type }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(type === 'success');

  const isPopupMode = sessionStorage.getItem('billing_popup_mode') === 'true';
  const handleRef = useRef(false);

  const sendPopupMessage = (message: Record<string, unknown>) => {
    window.opener?.postMessage(message, window.location.origin);
  };

  const cleanupStorage = () => {
    sessionStorage.removeItem('billing_popup_mode');
    sessionStorage.removeItem('billing_return_path');
    sessionStorage.removeItem('billing_pending_context');
  };

  useEffect(() => {
    if (handleRef.current) return;

    if (type === 'success') {
      handleRef.current = true;
      handleSuccess();
    }
  }, [type]);

  const handleSuccess = async () => {
    const authKey = searchParams.get('authKey');
    const pendingContextRaw = sessionStorage.getItem('billing_pending_context');

    if (!authKey || !pendingContextRaw) {
      if (isPopupMode) {
        sendPopupMessage({ type: 'CARD_REGISTER_FAIL', error: '카드 등록 정보가 올바르지 않습니다.' });
        cleanupStorage();
        window.close();
        return;
      }
      toastUtils.error('카드 등록 정보가 올바르지 않습니다.');
      setIsProcessing(false);
      return;
    }

    const { companyId } = JSON.parse(pendingContextRaw) as {
      companyId: number;
    };

    try {
      const result = await billingService.registerBillingKey({ authKey, companyId });

      if (isPopupMode) {
        cleanupStorage();
        if (result.success) {
          sendPopupMessage({ type: 'CARD_REGISTER_SUCCESS', companyId });
        } else {
          sendPopupMessage({ type: 'CARD_REGISTER_FAIL', error: result.message || '빌링키 등록에 실패했습니다.' });
        }
        window.close();
        return;
      }

      if (result.success) {
        toastUtils.success('결제 수단이 등록되었습니다.');
      } else {
        toastUtils.error(result.message || '빌링키 등록에 실패했습니다.');
      }
    } catch {
      if (isPopupMode) {
        cleanupStorage();
        sendPopupMessage({ type: 'CARD_REGISTER_FAIL', error: '빌링키 등록 중 오류가 발생했습니다.' });
        window.close();
        return;
      }
      toastUtils.error('빌링키 등록 중 오류가 발생했습니다.');
    } finally {
      setIsProcessing(false);
    }

    const returnPath = sessionStorage.getItem('billing_return_path');
    cleanupStorage();

    setTimeout(() => {
      navigate(returnPath || '/', { replace: true });
    }, 1500);
  };

  const handleFailRedirect = () => {
    if (isPopupMode) {
      const errorMessage = searchParams.get('message');
      sendPopupMessage({
        type: 'CARD_REGISTER_FAIL',
        error: errorMessage ? decodeURIComponent(errorMessage) : '카드 등록에 실패했습니다.',
      });
      cleanupStorage();
      window.close();
      return;
    }

    const returnPath = sessionStorage.getItem('billing_return_path');
    cleanupStorage();
    navigate(returnPath || '/', { replace: true });
  };

  const errorCode = searchParams.get('code');
  const errorMessage = searchParams.get('message');

  if (type === 'fail') {
    return (
      <Container>
        <Card>
          <IconWrapper $type="fail">
            <XCircle size={48} />
          </IconWrapper>
          <Title>카드 등록에 실패했습니다</Title>
          {errorMessage && <Description>{decodeURIComponent(errorMessage)}</Description>}
          {errorCode && <ErrorCode>오류 코드: {errorCode}</ErrorCode>}
          <ActionButton onClick={handleFailRedirect}>돌아가기</ActionButton>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Card>
        {isProcessing ? (
          <>
            <SpinnerWrapper>
              <Loader2 size={48} />
            </SpinnerWrapper>
            <Title>결제 수단을 등록하는 중...</Title>
            <Description>잠시만 기다려주세요.</Description>
          </>
        ) : (
          <>
            <IconWrapper $type="success">
              <CheckCircle size={48} />
            </IconWrapper>
            <Title>결제 수단이 등록되었습니다</Title>
            <Description>잠시 후 이전 페이지로 이동합니다.</Description>
          </>
        )}
      </Card>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: ${semanticColors.background.secondary};
  padding: 2rem;
`;

const Card = styled.div`
  background: ${semanticColors.background.primary};
  border-radius: 16px;
  padding: 3rem;
  text-align: center;
  max-width: 480px;
  width: 100%;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const IconWrapper = styled.div<{ $type: CallbackType }>`
  color: ${props => props.$type === 'success' ? colorTokens.brand[600] : '#ef4444'};
  margin-bottom: 0.5rem;
`;

const SpinnerWrapper = styled.div`
  color: ${colorTokens.brand[600]};
  margin-bottom: 0.5rem;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const Title = styled.h2`
  ${typography.heading.h4};
  color: ${semanticColors.text.primary};
  margin: 0;
`;

const Description = styled.p`
  ${typography.paragraph.p1.default};
  color: ${semanticColors.text.secondary};
  margin: 0;
`;

const ErrorCode = styled.span`
  ${typography.caption.caption1.default};
  color: ${semanticColors.text.tertiary};
  background: ${semanticColors.background.secondary};
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
`;

const ActionButton = styled.button`
  margin-top: 1rem;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  border: none;
  background: ${colorTokens.brand[600]};
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

export default BillingCallbackPage;
